import React, { memo, useState, useEffect, ChangeEvent, KeyboardEvent, useMemo } from 'react'
import classNames from 'classnames'
import moment from 'moment-timezone'
import uuid from 'uuid'
import { TextInput } from 'components/atoms'
import { useDropdownPosition } from 'utils/dropdownPosition'
import { usePrevious } from 'utils/usePrevious'
import { TimeSuggestions } from './components/TimeSuggestions'
import { TIME_INPUT_FORMAT } from './types'
import {
  getSuggestions,
  parseTime,
  buildDateTime,
  DEFAULT_SUGGESTION_STEP_MINUTES,
} from './utils/utils'
import 'scss/5_components/_time-input.scss'

export interface ITimeInputProps {
  value: string
  targetDate?: string
  anchorDateTime?: {
    date: string
    time: string
  }
  id?: string
  dataTest?: string
  placeholder?: string
  label?: string
  required?: boolean
  invalid?: boolean
  validationMessage?: string
  large?: boolean
  className?: string
  timeFormat?: TIME_INPUT_FORMAT
  disabled?: boolean
  minuteIncrement?: number
  onChange(change: ChangeEvent<HTMLInputElement>, dayDiff: number): void
  onBlur?: () => void
  ariaLabel?: string
  fullWidth?: boolean
  helperMessage?: string
  /**
   * @default false
   */
  clearable?: boolean
  onClearButtonClick?: Function
  /**
   * @default true
   */
  showSuggestionDropdown?: boolean
}

export const TimeInput = memo<ITimeInputProps>(
  ({
    value,
    targetDate,
    onChange,
    anchorDateTime: { date: anchorDate = '', time: anchorTime = '' } = {},
    id = uuid(),
    dataTest,
    placeholder,
    label,
    required,
    invalid,
    validationMessage,
    large,
    className = '',
    timeFormat = TIME_INPUT_FORMAT.MILITARY_SHORT,
    onBlur,
    disabled,
    minuteIncrement = DEFAULT_SUGGESTION_STEP_MINUTES, // 30
    ariaLabel,
    fullWidth = false,
    helperMessage,
    clearable = false,
    onClearButtonClick,
    showSuggestionDropdown = true,
  }) => {
    const [inputValue, setInputValue] = useState<string>(value)
    const [isInvalid, setIsInvalid] = useState<boolean>(false)
    const [guessedOption, setGuessedOption] = useState<string>('')
    const [showSuggestions, setShowSuggestions] = useState<boolean>(false)

    const anchorDateTime = useMemo(
      () =>
        anchorDate && anchorTime && targetDate
          ? buildDateTime(anchorDate, anchorTime, timeFormat)
          : undefined,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [anchorDate, anchorTime]
    )

    const suggestions: string[] = useMemo(
      () =>
        getSuggestions({
          guessedOption,
          anchorDateTime,
          timeFormat,
          minuteIncrement,
        }),
      [guessedOption, anchorDateTime, timeFormat, minuteIncrement]
    )

    const highlightedOption = useMemo(() => {
      if (guessedOption) return guessedOption

      if (value) return value

      // Add an hour to the anchor date time
      if (anchorDateTime) return anchorDateTime.clone().add(1, 'h').format(timeFormat)

      const now = moment()
      return now
        .clone()
        .subtract(now.minute() % minuteIncrement, 'm')
        .format(timeFormat)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [guessedOption, anchorDateTime, value])

    const hideSuggestions = () => {
      setGuessedOption('')
      setShowSuggestions(false)
    }

    const [inputRef, style] = useDropdownPosition(
      showSuggestions && !isInvalid,
      hideSuggestions,
      undefined,
      true,
      true
    )

    const changeHandler = ({ target: { value: newValue } }: ChangeEvent<HTMLInputElement>) => {
      if (clearable && newValue === '' && typeof onClearButtonClick === 'function') {
        onClearButtonClick()
      }

      setInputValue(newValue)
    }

    const optionSelectHandler = (newValue: string, dayDiff: number = 0) => {
      onChange({ target: { value: newValue, id } } as ChangeEvent<HTMLInputElement>, dayDiff)
      hideSuggestions()
      if (newValue && !isInvalid) {
        setIsInvalid(false)
      }
    }

    const focusHandler = () => {
      setGuessedOption(isInvalid ? '' : inputValue)
      setShowSuggestions(true)
      if (inputRef.current) inputRef.current.select()
    }

    const blurHandler = (event: React.FocusEvent<HTMLInputElement>) => {
      /**
       * If the user tabs or clicks away from the input,
       * event.relatedTarget will reference another dom element.
       *
       * However, if they click on a <TimeSuggestion /> element
       * the relatedTarget's parent will be <TimeSuggestions />.
       *
       * This is a simple/crude way to know whether the blur is caused by
       * a click of an element.
       *
       * If user clicks on a <TimeSuggestion />
       * then we should NOT trigger the onBlur change event below
       * because it would trigger onChange twice, first time being in
       * optionSelectHandler()
       */

      if (clearable && inputValue !== null) {
        hideSuggestions()
      }

      if (
        event.relatedTarget !== null &&
        (event.relatedTarget as any)?.parentNode?.className !== 'time-suggestions' &&
        !isInvalid
      ) {
        onChange(
          { target: { value: guessedOption || inputValue, id } } as ChangeEvent<HTMLInputElement>,
          0
        )
      }

      if (onBlur) {
        onBlur()
      }

      // Only select highlighted option if input was not cleared
      if (inputValue !== '') {
        // If time is parsed correctly but user goes away we need to set the option as a value
        optionSelectHandler(highlightedOption)
      }
    }

    const clickHandler = () => {
      if (!showSuggestions) setShowSuggestions(true)
    }

    const keyDownHandler = (event: KeyboardEvent<HTMLInputElement>) => {
      switch (event.key) {
        case 'Enter':
          optionSelectHandler(highlightedOption)
          break

        case 'Escape':
          hideSuggestions()
          break

        case 'Down':
        case 'ArrowDown': {
          event.preventDefault()
          if (!showSuggestions) setShowSuggestions(true)
          const nextIndex = suggestions.indexOf(highlightedOption) + 1
          setGuessedOption(suggestions[nextIndex === suggestions?.length ? 0 : nextIndex])
          break
        }

        case 'Up':
        case 'ArrowUp': {
          event.preventDefault()
          if (!showSuggestions) setShowSuggestions(true)
          const prevIndex = suggestions.indexOf(highlightedOption) - 1
          setGuessedOption(suggestions[prevIndex < 0 ? suggestions?.length - 1 : prevIndex])
          break
        }

        default:
          break
      }
    }

    /**
     * When the value prop updates, also update the inputValue
     * and update validation
     */
    useEffect(() => {
      if (value !== undefined) {
        setInputValue(value || '')
        setIsInvalid(false)
      } else if (inputValue && !isInvalid) {
        setInputValue(value)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value])

    /**
     * If the date prop has changed, calculate the new value
     * and trigger the onChange handler
     */
    const prevAnchorDateTime = usePrevious(anchorDateTime)
    useEffect(() => {
      if (anchorDateTime && prevAnchorDateTime && value) {
        const currentDateTime = buildDateTime(targetDate, value, timeFormat)
        const diffMinutes = currentDateTime ? currentDateTime.diff(prevAnchorDateTime, 'm') : 0
        const newValue = anchorDateTime.clone().add(diffMinutes, 'm')
        let dayDiff = 0
        if (targetDate && !newValue.isSame(targetDate, 'd')) {
          dayDiff = newValue.diff(targetDate, 'd') || (newValue.isBefore(targetDate) ? -1 : 1)
        }
        optionSelectHandler(newValue.format(timeFormat), dayDiff)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anchorDateTime])

    /**
     * When the input value updates, set the guessedOption
     * and update validation
     */
    useEffect(() => {
      if (!inputValue && isInvalid) {
        setIsInvalid(false)
      }

      if (inputValue !== value) {
        const parsed = parseTime(inputValue, timeFormat)
        setGuessedOption(parsed || '')
        setIsInvalid(!!inputValue && !parsed)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputValue])

    const textInputClassName = classNames('time-input-container', {
      'time-input-container-full-width': fullWidth,
    })

    return (
      <div className={className} data-test={`${dataTest}-container`}>
        <TextInput
          ref={inputRef}
          value={inputValue}
          onChange={changeHandler}
          onBlur={blurHandler}
          onKeyDown={keyDownHandler}
          onFocus={focusHandler}
          onClick={clickHandler}
          id={id}
          dataTest={dataTest}
          placeholder={placeholder || highlightedOption}
          label={label}
          required={required}
          invalid={isInvalid || invalid}
          validationMessage={isInvalid ? 'Invalid time' : validationMessage}
          clearable={clearable}
          className={textInputClassName}
          large={large}
          disabled={disabled}
          aria-label={ariaLabel}
          helperMessage={helperMessage}
          onClearButtonClick={onClearButtonClick}
        />
        {showSuggestionDropdown && (
          <TimeSuggestions
            dataTest={`${dataTest}-suggestions`}
            onOptionSelect={optionSelectHandler}
            options={suggestions}
            selectedOption={highlightedOption}
            targetDate={targetDate}
            anchorDateTime={anchorDateTime}
            style={style}
            timeFormat={timeFormat}
          />
        )}
      </div>
    )
  }
)

export default TimeInput
